import React, { useState, useEffect } from 'react';
import { reportsAPI, retailersAPI } from '../services/api';
// ...existing code...
import './ManagementPages.css';

const RetailerDetailsPage = () => {
  const [retailers, setRetailers] = useState([]);
  const [documentsMap, setDocumentsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agents, setAgents] = useState([]);

  useEffect(() => {
    fetchRetailerDetails();
  }, [selectedAgent]);

  const fetchAgents = async () => {
    try {
      // Fetch from agent collection summary to get all agents
      const data = await reportsAPI.getAgentCollectionSummary();
      const list = (data && (data.data || data)) || [];
      setAgents(list);
    } catch (err) {
      console.error('Error fetching agents:', err);
    }
  };

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchRetailerDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = selectedAgent ? { agentId: selectedAgent } : {};
      const data = await reportsAPI.getRetailerDetails(params);
      const list = (data && (data.data || data)) || [];
      setRetailers(list);
      // Fetch documents for each retailer
      const docsMap = {};
      await Promise.all(
        list.map(async (ret) => {
          try {
            const docs = await retailersAPI.getDocuments(ret.id);
            docsMap[ret.id] = docs;
          } catch (e) {
            docsMap[ret.id] = [];
          }
        })
      );
      setDocumentsMap(docsMap);
    } catch (err) {
      setError(err.message || 'Failed to load retailer details');
    } finally {
      setLoading(false);
    }
  };

  const getTotalStats = () => {
    if (!retailers || retailers.length === 0) return null;
    
    return {
      totalRetailers: retailers.length,
      totalDisbursed: retailers.reduce((sum, r) => sum + (r.total_disbursed || 0), 0),
      totalPayable: retailers.reduce((sum, r) => sum + (r.total_payable || 0), 0),
      totalCollected: retailers.reduce((sum, r) => sum + (r.total_collected || 0), 0),
      totalPending: retailers.reduce((sum, r) => sum + (r.pending_amount || 0), 0),
    };
  };

  const stats = getTotalStats();

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Retailer Details</h1>
        <p>Track loan disbursement, collections, and recovery status retailer-wise</p>
      </div>

      {/* Agent Filter */}
      <div className="filters" style={{ marginBottom: '20px' }}>
        <select
          value={selectedAgent || ''}
          onChange={(e) => setSelectedAgent(e.target.value || null)}
          style={{
            padding: '8px 12px',
            borderRadius: '6px',
            border: '1px solid #ddd',
            fontSize: '14px',
          }}
        >
          <option value="">All Agents</option>
          {agents.map((agent) => (
            <option key={agent.id} value={agent.id}>
              {agent.first_name} {agent.last_name}
            </option>
          ))}
        </select>
        <button
          className="button button-secondary"
          onClick={() => setSelectedAgent(null)}
          style={{ marginLeft: '10px' }}
        >
          Clear Filter
        </button>
      </div>

      {/* Overall Stats */}
      {stats && (
        <div className="grid grid-5" style={{ marginBottom: '30px' }}>
          <div className="dashboard-card">
            <div className="card-icon">🏪</div>
            <div className="card-content">
              <div className="card-label">Total Retailers</div>
              <div className="card-value">{stats.totalRetailers}</div>
            </div>
          </div>
          <div className="dashboard-card">
            <div className="card-icon">💳</div>
            <div className="card-content">
              <div className="card-label">Total Disbursed</div>
              <div className="card-value">₹{(stats.totalDisbursed / 100000).toFixed(2)}L</div>
            </div>
          </div>
          <div className="dashboard-card">
            <div className="card-icon">📊</div>
            <div className="card-content">
              <div className="card-label">Total Payable</div>
              <div className="card-value">₹{(stats.totalPayable / 100000).toFixed(2)}L</div>
            </div>
          </div>
          <div className="dashboard-card">
            <div className="card-icon">✅</div>
            <div className="card-content">
              <div className="card-label">Total Collected</div>
              <div className="card-value">₹{(stats.totalCollected / 100000).toFixed(2)}L</div>
            </div>
          </div>
          <div className="dashboard-card">
            <div className="card-icon">⚠️</div>
            <div className="card-content">
              <div className="card-label">Pending Amount</div>
              <div className="card-value">₹{(stats.totalPending / 100000).toFixed(2)}L</div>
            </div>
          </div>
        </div>
      )}

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div className="loader"></div>
      ) : retailers.length > 0 ? (
        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th>Retailer Name</th>
                <th>Phone</th>
                <th>Email</th>
                <th>City</th>
                <th>KYC Status</th>
                <th>Total Loans</th>
                <th>Active Loans</th>
                <th>Total Disbursed</th>
                <th>Total Payable</th>
                <th>Collected</th>
                <th>Pending Amount</th>
                <th>Collection %</th>
                <th>Documents</th>
              </tr>
            </thead>
            <tbody>
              {retailers.map((retailer) => {
                const payable = retailer.total_payable || 0;
                const collected = retailer.total_collected || 0;
                const collectionPercent = payable > 0 ? ((collected / payable) * 100).toFixed(2) : 0;
                let docs = documentsMap[retailer.id];
                if (!Array.isArray(docs)) docs = [];
                const getDoc = (type) => docs.find((d) => d.document_type === type);
                return (
                  <tr key={retailer.id}>
                    <td><strong>{retailer.name}</strong></td>
                    <td>{retailer.phone}</td>
                    <td>{retailer.email}</td>
                    <td>{retailer.city}</td>
                    <td>
                      <span className={`badge badge-${retailer.kyc_status?.toLowerCase() || 'pending'}`}>
                        {retailer.kyc_status || 'PENDING'}
                      </span>
                    </td>
                    <td>{retailer.total_loans || 0}</td>
                    <td><span className="badge badge-active">{retailer.active_loans || 0}</span></td>
                    <td>₹{(retailer.total_disbursed || 0).toLocaleString()}</td>
                    <td>₹{(payable / 100000).toFixed(2)}L</td>
                    <td>
                      <strong>₹{(collected / 100000).toFixed(2)}L</strong>
                    </td>
                    <td>
                      <span style={{ color: '#e74c3c' }}>
                        ₹{((retailer.pending_amount || 0) / 100000).toFixed(2)}L
                      </span>
                    </td>
                    <td>
                      <div style={{
                        background: '#ecf0f1',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        textAlign: 'center',
                        fontWeight: 'bold',
                        color: collectionPercent > 80 ? '#27ae60' : collectionPercent > 50 ? '#f39c12' : '#e74c3c'
                      }}>
                        {collectionPercent}%
                      </div>
                    </td>
                    <td>
                      {/* Document links */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {['PAN', 'AADHAAR', 'ELECTRICITY'].map((type) => {
                          const doc = getDoc(type);
                          return doc ? (
                            <a
                              key={type}
                              href={doc.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#0066cc', fontSize: '13px', textDecoration: 'underline' }}
                            >
                              {type}
                            </a>
                          ) : (
                            <span key={type} style={{ color: '#aaa', fontSize: '12px' }}>{type}-</span>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="no-data">No retailers found</div>
      )}

      <button 
        className="button button-primary" 
        onClick={() => fetchRetailerDetails()}
        style={{ marginTop: '20px' }}
      >
        Refresh
      </button>
    </div>
  );
};

export default RetailerDetailsPage;